import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTemplate } from '../../../../features/template/TemplateProvider';
import { ButtonCancel, ButtonPrimary } from '../../../../utils/Button';
import FocusControl from '../../../../utils/FocusControl';
import InputField, { SelectOption, addOption } from '../../../../utils/InputField';
import { mkRelativeUrl } from '../../../../utils/URL';
import { ApiGetActiveProductListOpe } from '../../../Settings/ProductList/ProductOperation';
import { handleNewAddSubscriptionOpe } from '../../../Subscriptions/SubscriptionsOperation';
import { ApiGetPricingServiceList, handleLoadPricing } from '../CustomerDetailsOperation';
import AppServiceList from './Comp/AppServiceList';
import PackageInfo from './Comp/PackageInfo';

const iniState = {
    // UserId
    // ApplicationId
    // SubscriptionName
    // DomainName
    // ProductId
    // PackageId
    // PriceId
    // Status
    // ServicePackageData[
    //     {
    //         Amount,
    //         AppServiceId,
    //         AppServicePackageId,
    //     }
    // ]

    UserId: 0,
    ApplicationId: 0,
    SubscriptionName: '',
    DomainName: '',
    PackageId: 0,
    PriceId: 0,
    Status: 'active',
    ServicePackageData: []
};

function AddNewSubscriptions({ data, closeModal }) {
    const navigate = useNavigate();
    const { setMessage } = useTemplate();
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState('');
    const [loading, setLoading] = useState(false);

    const [applicationList, setApplicationList] = useState([]); // Initial Application List
    const [applicationPackageList, setApplicationPackageList] = useState([]); // Application Package List
    const [appServicesList, setAppServicesList] = useState([]); // Initial Service List

    const [formData, setFormData] = useState({ ...iniState, UserId: data?.id });

    // Set Form Data on change
    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name) {
            setFormData((d) => ({ ...d, [name]: value }));
        }
    };

    // Set Application ID
    const handelSetApplicationId = (e) => {
        const { value } = e.target;

        const subscriptionName = applicationList.find(
            (item) => item?.id?.toString() === value
        )?.applicationTitle;
        setAppServicesList([]);

        setFormData((d) => ({
            ...d,
            ApplicationId: value,
            PackageId: 0,
            PriceId: 0,
            ServicePackageData: [],
            SubscriptionName: value ? `${subscriptionName} - ${data?.organization}` : ''
        }));
    };
    // Set Package Id
    const handelSetApplicationPackageId = (item) => {
        setFormData((d) => ({ ...d, PackageId: item?.packageId, PriceId: item?.priceId }));
    };

    // add new subscription
    const handleAddSubscription = (e) => {
        setLoading(true);
        e.preventDefault();
        handleNewAddSubscriptionOpe(formData, setIsError)
            .then(({ data: resData }) => {
                setLoading(false);
                navigate(mkRelativeUrl(`/billing/invoices/${resData?.invoiceId}/info`));
            })
            .catch((error) => {
                setLoading(false);
                setMessage({ type: 'error', text: error.toString(0) });
            });
    };

    // --Load Application List [Load when change application ID]
    useEffect(() => {
        setFormData((d) => ({
            ...d,
            ApplicationId: 0,
            PackageId: 0,
            PriceId: 0
        }));
        ApiGetActiveProductListOpe()
            .then(({ data: resData }) => {
                setApplicationList(resData.applicationList);
            })
            .catch((error) => {
                setMessage({ type: 'error', text: error.toString(0) });
            });
    }, [setMessage]);

    // Load Application Package & Pricing form API
    useEffect(() => {
        if (formData?.ApplicationId) {
            handleLoadPricing(data?.id, formData?.ApplicationId)
                .then(({ data: resData }) => {
                    setApplicationPackageList(resData.packagesList);
                })
                .catch((error) => {
                    setMessage({ type: 'error', text: error.toString(0) });
                });
        }
    }, [setMessage, formData?.ApplicationId, data?.id]);

    // Load services list
    useEffect(() => {
        // Fetch services list
        if (formData.UserId && formData.ApplicationId && formData.PackageId) {
            setIsLoading(true);
            setAppServicesList([]);
            ApiGetPricingServiceList(formData.UserId, formData.ApplicationId, formData.PackageId)
                .then(({ data: resData }) => {
                    setAppServicesList(resData.services);
                    setIsLoading(false);
                })
                .catch((err) => {
                    setIsLoading(false);
                    setMessage({ type: 'error', text: err.toString() });
                });
        }
    }, [setMessage, formData.UserId, formData.ApplicationId, formData.PackageId]);

    // Focus Control
    FocusControl(isError, setIsError, formData);

    return (
        <form className="flex flex-col gap-4" onSubmit={handleAddSubscription}>
            <SelectOption
                label="Application"
                name="ApplicationId"
                formData={formData}
                onChange={handelSetApplicationId}
                options={applicationList?.map((item) =>
                    addOption(item?.id, item?.applicationTitle, item?.id)
                )}
                required
                disabled={applicationList?.length === 0}
            />

            <InputField
                label="Subscription Name"
                placeholder="EX: Basic"
                name="SubscriptionName"
                onChange={handleChange}
                formData={formData}
                required
            />

            <PackageInfo
                applicationPackageList={applicationPackageList}
                handelSetApplicationPackageId={handelSetApplicationPackageId}
                formData={formData}
            />

            <AppServiceList
                isLoading={isLoading}
                appServicesList={appServicesList}
                formData={formData}
                setFormData={setFormData}
            />

            <InputField
                label="Domain"
                placeholder='EX: "www.example.com"'
                name="DomainName"
                onChange={handleChange}
                formData={formData}
                optional
            />

            <div className="w-full flex justify-end items-center gap-2 mb-2">
                <ButtonCancel title="Cancel" onClick={() => closeModal(false)} />
                <ButtonPrimary title="Create Invoice" btnType="submit" loading={loading} />
            </div>
        </form>
    );
}

export default AddNewSubscriptions;
